import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

type Accent = "primary" | "success" | "warning" | "info" | "secondary" | "error";

export function IconTile({
  icon,
  accent = "primary",
  size = 44,
  radius = 1.25,
  iconFontSize,
}: {
  icon: ReactNode;
  accent?: Accent;
  size?: number;
  radius?: number | string;
  iconFontSize?: number;
}) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        display: "grid",
        placeItems: "center",
        bgcolor: (theme) =>
          alpha(theme.palette[accent].main, theme.palette.mode === "dark" ? 0.16 : 0.08),
        color: `${accent}.main`,
        border: "1px solid",
        borderColor: `${accent}.main`,
        "& .MuiSvgIcon-root": { fontSize: iconFontSize, opacity: 0.92 },
      }}
    >
      {icon}
    </Box>
  );
}

export function StatusBadge({ label, accent }: { label: string; accent: Accent }) {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 26,
        px: 1.1,
        borderRadius: 1,
        bgcolor: (theme) =>
          alpha(theme.palette[accent].main, theme.palette.mode === "dark" ? 0.16 : 0.08),
        color: `${accent}.dark`,
        border: "1px solid",
        borderColor: `${accent}.main`,
        fontWeight: 700,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  accent = "primary",
  action,
  dense = false,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  accent?: Accent;
  action?: ReactNode;
  dense?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: dense ? { xs: 3, sm: 4 } : { xs: 5, sm: 7 },
        textAlign: "center",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.72),
        borderStyle: "dashed",
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        <IconTile
          icon={icon}
          accent={accent}
          size={dense ? 48 : 60}
          radius={dense ? 1.5 : 2}
          iconFontSize={dense ? 25 : 31}
        />
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        {hint && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "58ch" }}>
            {hint}
          </Typography>
        )}
        {action}
      </Stack>
    </Paper>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "flex-start" }}
      justifyContent="space-between"
      gap={2.5}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", mt: 0.85, maxWidth: "62ch" }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
  );
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <Stack spacing={1.25} aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 1.25, bgcolor: "grey.100" }} />
            <Stack spacing={0.8} sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  width: index % 2 === 0 ? "42%" : "58%",
                  height: 12,
                  borderRadius: 1,
                  bgcolor: "grey.200",
                }}
              />
              <Box sx={{ width: "28%", height: 9, borderRadius: 1, bgcolor: "grey.100" }} />
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
